import { useEffect } from 'react';
import { SITE_URL, SITE_NAME } from './siteConfig';

export interface SeoMeta {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  type?: 'website' | 'article';
  image?: string;
  noindex?: boolean;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  const selector = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useSeo(meta: SeoMeta): void {
  useEffect(() => {
    const path = meta.path.startsWith('/') ? meta.path : `/${meta.path}`;
    const url = `${SITE_URL}${path === '/' ? '/' : path}`;

    document.title = meta.title;

    upsertMeta('name', 'description', meta.description);
    if (meta.keywords) upsertMeta('name', 'keywords', meta.keywords);

    upsertLink('canonical', url);

    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:type', meta.type || 'website');
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    if (meta.image) upsertMeta('property', 'og:image', meta.image);

    upsertMeta('name', 'twitter:card', meta.image ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', meta.description);
    if (meta.image) upsertMeta('name', 'twitter:image', meta.image);

    upsertMeta('name', 'robots', meta.noindex ? 'noindex, nofollow' : 'index, follow');
  }, [meta.title, meta.description, meta.keywords, meta.path, meta.type, meta.image, meta.noindex]);
}